import {Edge, Gradient, PathLayout, Position} from "../types.ts";
import * as THREE from "three";
import {BufferGeometry, ShapeUtils, TypedArray, Vector2, Vector3} from "three";
import {EditId, NewEdge} from "../editing/editTypes.ts";
import {COMMON_HEIGHTS} from "../common_heights.ts";
import {minBy} from "lodash";
import area = ShapeUtils.area;

export type NumberArray = TypedArray | number[];
export const EPSILON = 0.000001

export const asClockwise = (path: NumberArray): NumberArray => {
  if (isPathClockwise(path)) {
    return path;
  }
  const result: number[] = [];
  for (let i = 0; i < path.length; i += 3) {
    result.unshift(path[i], path[i + 1], path[i + 2]);
  }
  return result;
};

export const isPathClockwise = (path: NumberArray, size = 3): boolean => {
  let sum = 0;
  for (let i = 0; i < path.length; i += size) {
    const curX = path[i];
    const curY = path[i + 1];
    const nextX = i === path.length - size ? path[0] : path[i + size];
    const nextY = i === path.length - size ? path[1] : path[i + size + 1];
    sum += (nextX - curX) * (nextY + curY);
  }
  return sum > 0;
};

export const isPositionArrayClockwise = (path: readonly Position[]): boolean => {
  let sum = 0;
  for (let i = 0; i < path.length; i += 1) {
    const curX = path[i].x;
    const curY = path[i].y;
    const nextX = i === path.length - 1 ? path[0].x : path[i + 1].x;
    const nextY = i === path.length - 1 ? path[0].y : path[i + 1].y;
    sum += (nextX - curX) * (nextY + curY);
  }
  return sum > 0;
};

export const determineUvsForPathLayoutToRotation = (path: NumberArray, rotation: number, scale = 0.125) => {
  const cosAngle = Math.cos(rotation);
  const sinAngle = Math.sin(rotation);

  const clockwisePath = asClockwise(path);
  const uvs = [];
  for (let i = 0; i < clockwisePath.length; i += 3) {
    const scaledX = clockwisePath[i] * scale;
    const scaledY = clockwisePath[i + 1] * scale;
    uvs.push(
      scaledX * cosAngle - scaledY * sinAngle,
      scaledX * sinAngle + scaledY * cosAngle
    );
  }
  return new Float32Array(uvs);
};

export const pathToLines = (path: readonly Position[], width: number, height?: number): LineInterface[] => {
  if (path == null) {
    return [];
  }
  const result = [];
  for (let i = 1; i < path.length; i += 1) {
    const endPosition = path[i];
    const startPosition = path[i - 1];
    result.push({
      startPosition,
      endPosition,
      width,
      height
    });
  }
  return result;
};

export interface LineInterface {
  startPosition: Position,
  endPosition: Position,
  width: number,
  height?: number
}

export const geomToLines = (random: () => number, bufferGeom: BufferGeometry, height: number, width: number, takePercentage = 1.0, result: LineInterface[] = []): LineInterface[] => {
  if (bufferGeom == null) {
    return [];
  }
  const positions = bufferGeom.attributes.position.array;

  const N = positions.length;
  let prev: Position = {x: positions[0], y: positions[1]};
  for (let i = 3; i < (N + 1); i += 3) {
    const current: Position = {
      x: positions[i % N],
      y: positions[(i + 1) % N]
    };
    if (random() < takePercentage) {
      result.push({
        startPosition: prev,
        endPosition: current,
        width,
        height
      });
    }
    prev = current;
  }
  return result;
};

export function longestSegment(bufferedPositions: NumberArray) {
  let distance = 0;
  let points: NumberArray = [];

  let i;
  let lastPointX = bufferedPositions[0];
  let lastPointY = bufferedPositions[1];
  for (i = 3; i < bufferedPositions.length; i += 3) {
    const deltaX = lastPointX - bufferedPositions[i];
    const deltaY = lastPointY - bufferedPositions[i + 1];
    const currentDistance = Math.hypot(deltaX * deltaX, deltaY * deltaY);
    if (currentDistance > distance) {
      distance = currentDistance;
      points = [lastPointX, lastPointY, bufferedPositions[i], bufferedPositions[i + 1]];
    }

    lastPointX = bufferedPositions[i];
    lastPointY = bufferedPositions[i + 1];
  }

  return points;
}

// Expects [x1, y1, z1, x2, y2, z2, ...] (aka Vector3)
export const determineUvsForPathLayoutRotatedToLongestEdge = (path: TypedArray | number[], additionalRotation = 0.0, stretched = false, scale = 0.125) => {
  const longestSeg = longestSegment(path);
  const point1X = longestSeg[0];
  const point1Y = longestSeg[1];
  const point2X = longestSeg[2];
  const point2Y = longestSeg[3];

  const rotation = (-Math.atan2(point1Y - point2Y, point1X - point2X) + additionalRotation) % (Math.PI * 2);
  const quarter = Math.PI / 2;
  if (stretched) {
    const finalRotation = rotation < quarter || rotation > quarter * 3 ? rotation : rotation + Math.PI;
    return stretchedUvsForPathLayout(path, finalRotation);
  }
  return determineUvsForPathLayoutToRotation(path, rotation, scale);
};

// Expects [x1, y1, z1, x2, y2, z2, ...] (aka Vector3)
export const determineUvsForPathLayoutRotatedToFirstEdge = (path: TypedArray | number[], additionalRotation = 0.0, scale = 0.125) => {
  const point1X = path[0];
  const point1Y = path[1];
  const point2X = path[3];
  const point2Y = path[4];

  const rotation = (-Math.atan2(point1Y - point2Y, point1X - point2X) + additionalRotation) % (Math.PI * 2);

  return determineUvsForPathLayoutToRotation(path, rotation, scale);
};

export const stretchedUvsForPathLayout = (path: TypedArray | number[], rotation: number) => {
  const cosAngle = Math.cos(rotation);
  const sinAngle = Math.sin(rotation);

  const clockwisePath = asClockwise(path);

  const rotated = [];
  for (let i = 0; i < clockwisePath.length; i += 3) {
    const x = clockwisePath[i];
    const y = clockwisePath[i + 1];
    const rotatedX = x * cosAngle - y * sinAngle;
    const rotatedY = x * sinAngle + y * cosAngle;
    rotated.push([rotatedX, rotatedY]);
  }

  let [minX, minY] = rotated[0];
  let maxX = minX;
  let maxY = minY;
  for (let i = 1; i < rotated.length; i += 1) {
    const [x, y] = rotated[i];
    if (x < minX) {
      minX = x;
    } else if (x > maxX) {
      maxX = x;
    }
    if (y < minY) {
      minY = y;
    } else if (y > maxY) {
      maxY = y;
    }
  }

  const rangeX = maxX - minX;
  const rangeY = maxY - minY;

  const uvs: number[] = [];
  rotated.forEach(([x, y]) => {
    const lerpX = (x - minX) / rangeX;
    const lerpY = (y - minY) / rangeY;
    uvs.push(
      lerpX,
      lerpY
    );
  });
  return new Float32Array(uvs);
};


const closePointVector = new Vector3()
const LINE = new THREE.Line3();

export interface BasicEdge {
  startPosition: Position,
  endPosition: Position,
}

export function formatEdge(edge: BasicEdge): string {
  return `(${edge.startPosition.x},${edge.startPosition.y}),(${edge.endPosition.x},${edge.endPosition.y})`
}

export const closestPointToEdge = (edge: BasicEdge, point: Position): Vector3 => {
  const pointX = new THREE.Vector3(edge.startPosition.x, edge.startPosition.y, COMMON_HEIGHTS.EDGES);
  const pointY = new THREE.Vector3(edge.endPosition.x, edge.endPosition.y, COMMON_HEIGHTS.EDGES);
  LINE.set(pointX, pointY);

  closePointVector.setX(point.x)
  closePointVector.setY(point.y)

  return LINE.closestPointToPoint(closePointVector, true, new THREE.Vector3());
};

export const distanceToEdge = (edge: BasicEdge, point: Position): number => {
  const pointX = new THREE.Vector3(edge.startPosition.x, edge.startPosition.y, COMMON_HEIGHTS.EDGES);
  const pointY = new THREE.Vector3(edge.endPosition.x, edge.endPosition.y, COMMON_HEIGHTS.EDGES);
  LINE.set(pointX, pointY);

  closePointVector.setX(point.x)
  closePointVector.setY(point.y)

  const result = LINE.closestPointToPoint(closePointVector, true, new THREE.Vector3());
  return result.distanceTo(closePointVector)
};


export const getCloseIntersection = (edge: NewEdge | Edge, point: Vector3): EditId | null => {
  const startDistance = distanceBetweenPointsSquared(edge.startPosition, point);
  const endDistance = distanceBetweenPointsSquared(edge.endPosition, point);

  if (startDistance < endDistance && startDistance < 5) {
    return edge.startIntersection;
  }
  if (endDistance < 5) {
    return edge.endIntersection;
  }
  return null;
};

export const distanceBetweenPointsSquared = (p1: Position, p2: Position): number => (p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2;
export const distanceBetweenPoints = (p1: Position, p2: Position): number => Math.sqrt(distanceBetweenPointsSquared(p1, p2));

export const angleBetweenPoints = (start: Position, middle: Position, end: Position) => angleBetweenLines(start.x, start.y, middle.x, middle.y, middle.x, middle.y, end.x, end.y);

export const angleBetweenLines = (line1x1: number, line1y1: number, line1x2: number, line1y2: number, line2x1: number, line2y1: number, line2x2: number, line2y2: number) => {
  const startX = line1x2 - line1x1;
  const startY = line1y2 - line1y1;

  const endX = line2x2 - line2x1;
  const endY = line2y2 - line2y1;

  const dot = startX * endX + startY * endY;
  const det = startX * endY - startY * endX;

  return Math.atan2(det, dot);
};

export function addVector(position1: Position, position2: Position): Position {
  return {
    x: position1.x + position2.x,
    y: position1.y + position2.y,
  }
}

export function firstIntersection(lineA: Position, lineB: Position, layout: PathLayout): Position | undefined {
  const intersectionPoints: Position[] = [];
  for (let i = 0; i < layout.path.length; i++) {
    const current = layout.path[i];
    const next = layout.path[(i + 1) % layout.path.length];

    const intersectionPoint = lineIntersectionPoint(lineA, lineB, current, next)
    if (intersectionPoint != null && intersectionPoint != 'collinear') {
      intersectionPoints.push(intersectionPoint)
    }
  }

  return minBy(intersectionPoints, p => distanceBetweenPointsSquared(lineA, p))
}

export function pathLayoutArea(pathLayout: PathLayout): number {
  return Math.abs(area(pathLayout.path.map(p => ({...p}))));
}

export function pathLayoutContains(layout: PathLayout, point: Position): boolean {
  const path = layout.path;
  const x = point.x;
  const y = point.y;

  let inside = false;
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
    const xi = path[i].x, yi = path[i].y;
    const xj = path[j].x, yj = path[j].y;

    const intersect = ((yi > y) !== (yj > y))
      && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}


export interface BoundingBox {
  top: Position,
  left: Position,
  right: Position,
  bottom: Position
}

export function getBoundingBox(positions: readonly Position[]): BoundingBox {
  let top = {x: 0, y: Number.NEGATIVE_INFINITY}
  let left = {x: Number.POSITIVE_INFINITY, y: 0}
  let right = {x: Number.NEGATIVE_INFINITY, y: 0}
  let bottom = {x: 0, y: Number.POSITIVE_INFINITY}

  positions.forEach(p => {
    if (p.y > top.y) {
      top = p
    }
    if (p.y < bottom.y) {
      bottom = p
    }
    if (p.x > right.x) {
      right = p
    }
    if (p.x < left.x) {
      left = p
    }
  })

  return {
    top: new Vector2(top.x, top.y),
    left: new Vector2(left.x, left.y),
    right: new Vector2(right.x, right.y),
    bottom: new Vector2(bottom.x, bottom.y)
  };
}

function boundingBoxesIntersectOrTouch(b1: BoundingBox, b2: BoundingBox): boolean {
  if (b1.top.x + EPSILON < b2.bottom.x || b1.bottom.x - EPSILON > b2.top.x) return false
  if (b1.top.y + EPSILON < b2.bottom.y || b1.bottom.y - EPSILON > b2.top.y) return false
  return true
}

/**
 * Returns a perpendicular that is to the left of the original line - will point inside a counter-clockwise oriented layout
 */
export function perpendicularVector(startPosition: Position, endPosition: Position, scaleToLength: number): Position {
  const perpendicularGradient = {x: -(endPosition.y - startPosition.y), y: endPosition.x - startPosition.x};
  const scalar = scaleToLength / vectorLength(perpendicularGradient)
  return {x: perpendicularGradient.x * scalar, y: perpendicularGradient.y * scalar}
}

export function reverseVector(vec: Position): Position {
  return { x: -vec.x, y: -vec.y }
}

function vectorLength(position: Position): number {
  return Math.hypot(position.x, position.y)
}

export function lineLength(p1: Position, p2: Position): number {
  return Math.hypot(p2.x - p1.x, p2.y - p1.y)
}

export function center(p1: Position, p2: Position): Position {
  return {x: (p2.x + p1.x) / 2, y: (p2.y + p1.y) / 2}
}

export function prettyPrint2(arr: number[] | TypedArray, size = 2) {
  const resultPositions: string[] = []
  for (let i = 0; i < arr.length; i += size) {
    resultPositions.push(`(${arr[i]},${arr[i + 1]})`)
  }
  console.log(resultPositions.join(","));
}

export function prettyPrint3(arr: number[]) {
  const resultPositions: string[] = []
  for (let i = 0; i < arr.length; i += 3) {
    resultPositions.push(`(${arr[i]},${arr[i + 1]}, ${arr[i + 2]})`)
  }
  console.log(resultPositions.join(","));
}

export function squaredDistance(position1: Position, position2: Position): number {
  const dx = position1.x - position2.x;
  const dy = position1.y - position2.y;
  return dx * dx + dy * dy;
}

export function distance(position1: Position, position2: Position): number {
  return Math.sqrt(squaredDistance(position1, position2))
}

export function format2dp(num: number): string {
  return (Math.round(num * 100) / 100).toFixed(2);
}

export function lineIntersectionPointIgnoreCollinear(a: Position, b: Position, c: Position, d: Position): Position | null {
  const result = lineIntersectionPoint(a, b, c, d);
  if (result == 'collinear') {
    return null;
  }
  return result;
}

export function equalsEpsilon(n1: number, n2: number): boolean {
  return Math.abs(n1 - n2) < EPSILON;
}

/** Lines AB and CD. If collinear, lines are touching. */
export function lineIntersectionPoint(a: Position, b: Position, c: Position, d: Position): Position | null | 'collinear' {
  // Sign of areas correspond to which side of ab points c and d are
  // Sign of areas correspond to which side of ab points c and d are
  const a1 = signed2DTriArea(a, b, d) // Compute winding of abd (+ or -)
  const a2 = signed2DTriArea(a, b, c) // To intersect, must have sign opposite of a1

  const a1Collinear = -EPSILON < a1 && a1 < EPSILON;
  const a2Collinear = -EPSILON < a2 && a2 < EPSILON;

  if (a1Collinear && a2Collinear) {
    const b1 = getBoundingBox([a, b])
    const b2 = getBoundingBox([c, d])
    if (!boundingBoxesIntersectOrTouch(b1, b2)) {
      return null
    }

    // Lines are collinear
    return 'collinear'
  }

  // If c and d are on different sides of ab, areas have different signs
  if (a1Collinear || a2Collinear || (a1 < 0 && a2 > 0) || a2 < 0 && a1 > 0) {
    // Compute signs for a and b with respect to segment cd
    const a3 = signed2DTriArea(c, d, a) // Compute winding of cda (+ or -)
    // Since area is constant a1 - a2 = a3 - a4, or a4 = a3 + a2 - a1
    // float a4 = Signed2DTriArea(c, d, b); // Must have opposite sign of a3
    const a4 = (a3 + a2 - a1)
    // Points a and b on different sides of cd if areas have different signs
    if (a3 * a4 < EPSILON) {
      // Segments intersect. Find intersection point along L(t) = a + t * (b - a).
      // Given height h1 of an over cd and height h2 of b over cd,
      // t = h1 / (h1 - h2) = (b*h1/2) / (b*h1/2 - b*h2/2) = a3 / (a3 - a4),
      // where b (the base of the triangles cda and cdb, i.e., the length
      // of cd) cancels out.
      const t = a3 / (a3 - a4)
      const px = a.x + t * (b.x - a.x)
      const py = a.y + t * (b.y - a.y)
      return {x: px, y: py}
    }
  }

  // Segments not intersecting (or collinear)
  return null;
}

function signed2DTriArea(a: Position, b: Position, c: Position): number {
  return (a.x - c.x) * (b.y - c.y) - (a.y - c.y) * (b.x - c.x)
}

export function forPolygonPairwise<T>(arr: T[] | readonly T[], func: (a: T, b: T) => void) {
  for (let i = 0; i < arr.length; i++) {
    if (i === arr.length - 1) {
      func(arr[i], arr[0])
    } else {
      func(arr[i], arr[i + 1])
    }
  }
}

export function polygonPairwise<T>(arr: T[] | readonly T[]): Array<{ e1: T, e2: T }> {
  const result: Array<{ e1: T, e2: T }> = []
  for (let i = 0; i < arr.length; i++) {
    if (i === arr.length - 1) {
      result.push({e1: arr[i], e2: arr[0]})
    } else {
      result.push({e1: arr[i], e2: arr[i + 1]})
    }
  }
  return result
}

export function zipWithNext<T>(arr: T[] | readonly T[]): Array<{ e1: T, e2: T }> {
  const result: Array<{ e1: T, e2: T }> = []
  for (let i = 0; i < arr.length - 1; i++) {
    result.push({e1: arr[i], e2: arr[i + 1]})
  }
  return result
}

export function gradient(p1: Position, p2: Position): Gradient {
  return {dx: p2.x - p1.x, dy: p2.y - p1.y}
}

export function unitGradient(p1: Position, p2: Position): number {
  const g = gradient(p1, p2)
  if (g.dx == 0.0) {
    return Number.POSITIVE_INFINITY
  } else {
    return g.dy / g.dx
  }
}
